import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Grid, Card, CardContent, Typography, TextField, FormControl,
    InputLabel, Select, MenuItem, Snackbar, Alert
} from "@mui/material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import { useMantenimiento } from "../../hooks/useMantenimiento";
import * as cargoService from "../../services/cargoService";
import GrupoCargoAutocomplete from "../../components/common/GrupoCargoAutocomplete";

/**
 * Pantalla de mantenimiento para Cargo.
 * Sigue la plantilla estándar del sistema:
 * - Botón Guardar centrado arriba y abajo (vía MantenimientoForm).
 * - Enlace Volver a la navegabilidad /cargo.
 * - Tarjeta con datos básicos (Grupo Cargo, Código, Descripción, Estado) y validaciones.
 * - Tarjeta con información de auditoría visible en edición (id > 0).
 */
const Edit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const id = parseInt(params.id || 0, 10);
    const isEdicion = id > 0;

    const { isReadOnly, setIsReadOnly } = useMantenimiento("trabajador");

    const [idGrupoCargo, setIdGrupoCargo] = useState(1);
    const [codigo, setCodigo] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [idEstado, setIdEstado] = useState(2);

    const [audit, setAudit] = useState({
        fechaRegistro: null,
        usuarioRegistro: "",
        fechaModificacion: null,
        usuarioModificacion: "",
    });

    const [cargando, setCargando] = useState(false);
    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const mostrarMensaje = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    };

    const fetchEntity = useCallback(async () => {
        if (!isEdicion) {
            setIdGrupoCargo(1);
            setCodigo("");
            setDescripcion("");
            setIdEstado(2);
            setAudit({
                fechaRegistro: null,
                usuarioRegistro: "",
                fechaModificacion: null,
                usuarioModificacion: "",
            });
            return;
        }

        setCargando(true);
        try {
            const data = await cargoService.getCargoViewById(id);
            if (data) {
                setIdGrupoCargo(data.idGrupoCargo || 1);
                setCodigo(data.codigo || "");
                setDescripcion(data.descripcion || "");
                setIdEstado(data.idEstado ?? 2);
                setAudit({
                    fechaRegistro: data.fechaRegistro || null,
                    usuarioRegistro: data.usuarioRegistro || "",
                    fechaModificacion: data.fechaModificacion || null,
                    usuarioModificacion: data.usuarioModificacion || "",
                });
            }
        } catch (err) {
            console.error("Error al cargar cargo:", err);
            mostrarMensaje("error", "Error al cargar la información del cargo");
        }
        setCargando(false);
    }, [id, isEdicion]);

    useEffect(() => {
        fetchEntity();
    }, [fetchEntity]);

    const postEntity = async () => {
        if (!idGrupoCargo) {
            mostrarMensaje("error", "Debe seleccionar un Grupo de Cargo");
            return;
        }
        if (!codigo || codigo.trim() === "") {
            mostrarMensaje("error", "El código del cargo es obligatorio");
            return;
        }
        if (!descripcion || descripcion.trim() === "") {
            mostrarMensaje("error", "La descripción del cargo es obligatoria");
            return;
        }

        try {
            const resultId = await cargoService.postCargo({
                id: id,
                idGrupoCargo: idGrupoCargo,
                codigo: codigo.trim().toUpperCase(),
                descripcion: descripcion.trim(),
                idEstado: idEstado,
                fechaRegistro: audit.fechaRegistro,
                usuarioRegistro: audit.usuarioRegistro,
            });

            const savedId = typeof resultId === "number" ? resultId : (resultId?.data || id);
            if (savedId > 0) {
                mostrarMensaje("success", "Se guardaron los datos con éxito");
                setIsReadOnly(true);

                if (!isEdicion && savedId > 0) {
                    navigate("/cargo/edit/" + savedId);
                } else {
                    fetchEntity();
                }
            } else {
                mostrarMensaje("error", "No se pudo guardar el cargo");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || "Error al guardar";
            mostrarMensaje("error", msg);
        }
    };

    return (
        <div>
            <MantenimientoForm
                title={(isEdicion ? "Editar" : "Nuevo") + " Cargo"}
                backTo="/cargo"
                onSave={postEntity}
                disabled={isReadOnly || cargando}
            >
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* Tarjeta de Datos Básicos */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                                    Datos Básicos
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <GrupoCargoAutocomplete
                                            value={idGrupoCargo}
                                            onChange={(val) => setIdGrupoCargo(val)}
                                            disabled={isReadOnly}
                                            soloActivos={false}
                                            label="Grupo de Cargo *"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Código"
                                            value={codigo}
                                            onChange={(e) => setCodigo(e.target.value)}
                                            disabled={isReadOnly}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ maxLength: 20 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel shrink>Estado</InputLabel>
                                            <Select
                                                label="Estado"
                                                value={idEstado}
                                                onChange={(e) => setIdEstado(Number(e.target.value))}
                                                disabled={isReadOnly}
                                            >
                                                <MenuItem value={2}>Activo</MenuItem>
                                                <MenuItem value={1}>Anulado</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Descripción"
                                            value={descripcion}
                                            onChange={(e) => setDescripcion(e.target.value)}
                                            disabled={isReadOnly}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            <Snackbar
                open={openMessage}
                autoHideDuration={5000}
                onClose={() => setOpenMessage(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setOpenMessage(false)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Edit;

